import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  TextField,
  Box
} from '@mui/material';
import { Error as ErrorIcon } from '@mui/icons-material';
import { useOrganization } from '../../../contexts/OrganizationContext';

const DeleteOrganizationDialog = ({ open, onClose, organization }) => {
  const { deleteOrganization, loading } = useOrganization();
  const [error, setError] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');

  const handleClose = () => {
    setError(null);
    setConfirmationText('');
    onClose();
  };

  const handleConfirm = async () => {
    if (confirmationText !== organization.name) {
      setError('Organization name does not match');
      return;
    }

    setError(null);

    try {
      await deleteOrganization(organization.id);
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to delete organization');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && confirmationText === organization.name) {
      handleConfirm();
    }
  };

  if (!organization) return null;

  const isConfirmationValid = confirmationText === organization.name;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" />
          Delete Organization
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Warning:</strong> This action cannot be undone. All environments, data, and settings associated with this organization will be permanently deleted.
        </Alert>

        <DialogContentText sx={{ mb: 2 }}>
          Are you sure you want to permanently delete the organization <strong>"{organization.name}"</strong>?
        </DialogContentText>

        <DialogContentText sx={{ mb: 2 }}>
          To confirm, please type the organization name exactly as shown above:
        </DialogContentText>

        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          placeholder={organization.name}
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          onKeyPress={handleKeyPress}
          error={!!error && error === 'Organization name does not match'}
          disabled={loading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={loading || !isConfirmationValid}
        >
          {loading ? 'Deleting...' : 'Delete Permanently'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteOrganizationDialog;
